import { ImageIcon, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ImageLightbox } from "@/components/chat/ImageLightbox";
import { getApiBaseUrl } from "@/config/runtime";
import { getHeaders } from "@/lib/api";
import type { MessageAttachment } from "@/types";

/**
 * Display variants:
 * - `"thumbnail"` — small 80x80 square (for user-uploaded images)
 * - `"generated"` — large prominent card (for AI-generated images)
 */
type ImageVariant = "thumbnail" | "generated";

interface ImageAttachmentThumbnailProps {
  attachment: MessageAttachment;
  /** Display variant. Defaults to `"thumbnail"`. */
  variant?: ImageVariant;
}

// ---------------------------------------------------------------------------
// Attachment URL fetching helpers
// ---------------------------------------------------------------------------

interface FetchContext {
  isCancelled: () => boolean;
  onObjectUrl: (url: string) => void;
}

/** Resolve a JSON response from the attachment endpoint to a displayable URL. */
async function resolveJsonUrl(
  // NOSONAR -- URL resolution with multiple response types
  json: { data?: { url?: string } },
  apiBase: string,
  headers: HeadersInit,
  ctx: FetchContext,
): Promise<string | null> {
  const resolvedUrl: string = json?.data?.url ?? "";
  if (!resolvedUrl) throw new Error("No URL in response");
  if (ctx.isCancelled()) return null;

  // Full URL (e.g. SAS URL) — use directly
  if (resolvedUrl.startsWith("http://") || resolvedUrl.startsWith("https://")) {
    return resolvedUrl;
  }

  // Relative URL (local storage) — fetch raw bytes with auth
  const fileResp = await fetch(`${apiBase}${resolvedUrl}`, { headers });
  if (!fileResp.ok) throw new Error(`HTTP ${fileResp.status}`);
  const blob = await fileResp.blob();
  if (ctx.isCancelled()) return null;
  const objectUrl = URL.createObjectURL(blob);
  ctx.onObjectUrl(objectUrl);
  return objectUrl;
}

/** Fetch attachment data and return a displayable image URL. */
async function fetchAttachmentUrl(uuid: string, ctx: FetchContext): Promise<string | null> {
  const headers = await getHeaders();
  const apiBase = getApiBaseUrl();

  const resp = await fetch(`${apiBase}/chat/attachments/${uuid}`, { headers, cache: "no-cache" });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  if (ctx.isCancelled()) return null;

  const contentType = resp.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const json = await resp.json();
    return resolveJsonUrl(json, apiBase, headers, ctx);
  }

  // Response is already raw image bytes (legacy / cached)
  const blob = await resp.blob();
  if (ctx.isCancelled()) return null;
  const objectUrl = URL.createObjectURL(blob);
  ctx.onObjectUrl(objectUrl);
  return objectUrl;
}

/**
 * Renders an image attachment with lazy-loading from the backend.
 * Clicking opens a lightbox overlay instead of navigating to a new tab.
 */
export function ImageAttachmentThumbnail({
  // NOSONAR -- variant-based rendering with loading/error/loaded states
  attachment,
  variant = "thumbnail",
}: Readonly<ImageAttachmentThumbnailProps>) {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    fetchAttachmentUrl(attachment.uuid, {
      isCancelled: () => cancelled,
      onObjectUrl: (url) => {
        objectUrl = url;
      },
    })
      .then((url) => {
        if (!cancelled && url) setDisplayUrl(url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.uuid]);

  // ── Error state ──────────────────────────────────────────────────────────
  if (error) {
    const sizeClasses = variant === "generated" ? "w-80 h-56 rounded-xl" : "w-20 h-20 rounded-lg";
    return (
      <div
        className={`${sizeClasses} bg-surface-secondary border border-border-secondary flex items-center justify-center`}
      >
        <ImageIcon className="w-6 h-6 text-content-quaternary" />
      </div>
    );
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (!displayUrl) {
    if (variant === "generated") {
      return (
        <div className="w-80 h-56 rounded-xl bg-surface-secondary border border-border-secondary flex items-center justify-center animate-pulse">
          <Loader2 className="w-8 h-8 text-content-quaternary animate-spin" />
        </div>
      );
    }
    return (
      <div className="w-20 h-20 rounded-lg bg-surface-secondary border border-border-secondary flex items-center justify-center animate-pulse">
        <Loader2 className="w-5 h-5 text-content-quaternary animate-spin" />
      </div>
    );
  }

  // ── Loaded state ─────────────────────────────────────────────────────────
  if (variant === "generated") {
    return (
      <>
        <button
          onClick={() => setLightboxOpen(true)}
          className="group relative rounded-xl overflow-hidden border border-border-secondary hover:border-brand-primary transition-all cursor-pointer shadow-lg hover:shadow-xl"
          title="Click to view full size"
        >
          <img src={displayUrl} alt={attachment.filename} className="max-w-sm max-h-96 object-contain bg-black/5" />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">
              Click to expand
            </div>
          </div>
        </button>

        {lightboxOpen && (
          <ImageLightbox src={displayUrl} alt={attachment.filename} onClose={() => setLightboxOpen(false)} />
        )}
      </>
    );
  }

  // Default: thumbnail variant
  return (
    <>
      <button
        onClick={() => setLightboxOpen(true)}
        className="group relative rounded-lg overflow-hidden border border-border-secondary hover:border-brand-primary transition cursor-pointer"
        title="Click to view full size"
      >
        <img src={displayUrl} alt={attachment.filename} className="w-20 h-20 object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
      </button>

      {lightboxOpen && (
        <ImageLightbox src={displayUrl} alt={attachment.filename} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
}
